import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ReceiptViewGateway from '../../../gateway/Receipt/ReceiptViewGateway'
import { ReceiptView } from '../../../domain/Receipt/ReceiptView'
import { Tab, Tabs } from '../tab/Tab'
import IntegrationStateHistoryBlock from '../integrationState/IntegrationStateHistoryBlock'
import ReceiptsValidation from '../integrationState/ReceiptsValidation'

// Styles
import '../../../assets/styles/components/_validation.scss'
import ViewDossierGateway from '../../../gateway/ViewDossier/ViewDossierGateway'
import RetrieveViewDossierUseCase from '../../../useCase/Folder/RetrieveViewDossierUseCase'
import PresentationDossier from '../integrationState/PresentationDossier'
import { ViewPageInterface } from '../../../domain/ViewDossier/ViewPage'
import FetchFolderGateway from '../../../gateway/Folder/FetchFolderGateway'
import { SignedDocument } from '../../../domain/Docusign/SignedDocument'
import GetListSignedDocumentUseCase from '../../../useCase/Docusign/GetListSignedDocumentUseCase'
import FetchSignedDocumentGateway from '../../../gateway/Docusign/FetchSignedDocumentGateway'
import { FolderDetail } from '../../../domain/Folder/Folder'
import FooterValidation from '../integrationState/FooterValidation'
import InvalidElement from '../../../domain/Folder/InvalidElementInterface'
import ErrorModal from '../modal/ErrorModal'
import GetIntegrationStateHistoryUseCase from '../../../useCase/Folder/GetIntegrationStateHistoryUseCase'
import IntegrationStateHistoryGateway from '../../../gateway/Folder/IntegrationStateHistoryGateway'
import { IntegrationStateHistoryInterface } from '../../../domain/IntegrationStateHistory/IntegrationStateHistory'
import { EFolderStatus } from '../../../domain/Folder/EFolderStatus'
import FolderDetailsValidation from '../folder/FolderComponent/FolderDetailsValidation'

interface Props {
  dossierId: string
}

const BlockPreValidationSummaryComponent = ({ dossierId }: Props): JSX.Element => {
  const { t } = useTranslation()

  const [dossierSummary, setDossierSummary] = useState<FolderDetail | null>(null)
  const [documents, setDocuments] = useState<SignedDocument[] | null>([])
  const [receipts, setReceipts] = useState<{ [key: string]: ReceiptView[] } | null>(null)
  const [integrationStateHistory, setIntegrationStateHistory] = useState<IntegrationStateHistoryInterface[] | null>(null)
  const [dossierPresentation, setDossierPresentation] = useState<ViewPageInterface[] | null>(null)
  const [state, setState] = useState<string | null>(null)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [invalidElements, setInvalidElements] = useState<InvalidElement[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    new FetchFolderGateway().getSummary(dossierId).then((response) => {
      setDossierSummary(response)
      const state = t(`common.${response?.status}`)
      setState(state)
    })

    new ReceiptViewGateway().retrieveList(dossierId).then((response) => {
      setReceipts(response)
    })

    new GetListSignedDocumentUseCase(new FetchSignedDocumentGateway()).execute(dossierId).then((response) => {
      setDocuments(response)
    })

    const getIntegrationStateHistoryUseCase = new GetIntegrationStateHistoryUseCase(new IntegrationStateHistoryGateway())
    getIntegrationStateHistoryUseCase.handle({ dossierId: dossierId }).then((response) => {
      setIntegrationStateHistory(response)
    })

    const retrieveViewDossierUseCase = new RetrieveViewDossierUseCase(new ViewDossierGateway())
    retrieveViewDossierUseCase.handle({ dossierId: dossierId }).then((response) => {
      if (response !== null) {
        setDossierPresentation(response)
      }
    })

  }, [dossierId])

  const updateAllReceiptsState = async (state: string, reason: string | null) => {
    setIsLoading(true)
    const folderGateway = new FetchFolderGateway()

    folderGateway.updateDossierSubmitPreSignatureValidationStatus(dossierId, state, reason, invalidElements).then(response => {
      if (response) {
        window.location.href = `/pre-signature-validation`
      } else {
        setErrorMessage(t('error.error-occured'))
      }
    }).finally(() =>
      setIsLoading(false)
    )
  }

  const findIndexByCombinedId = (elements: InvalidElement[], targetElementId: string, targetReceiptId: string | null): number => {
      return elements.findIndex(
        (element) => element.elementId === targetElementId && element.receiptId === targetReceiptId
      );
    };

  const handleInvalidateToggle = (isInvalid: boolean, elementId: string, comment = "", blockId: string, receiptId: string | null, value: string | null, pageId: string) => {
    setInvalidElements(prevElements => {
      const index = findIndexByCombinedId(prevElements, elementId, receiptId);

      if (isInvalid) {
        if (index === -1) {
          return [...prevElements, { comment, elementId, blockId, receiptId, value, pageId }];
        }
        else {
          return prevElements.map((doc, i) =>
            i === index ? { ...doc, comment } : doc
          );
        }
      } else {
        return prevElements.filter((_, i) => i !== index);
      }
    });
  };

  const handleCommentChange = (elementId: string, receiptId: string | null, newComment: string) => {
    setInvalidElements(prevElements => {
      const index = findIndexByCombinedId(prevElements, elementId, receiptId);
      if (index === -1) return prevElements;

      return prevElements.map((doc, i) =>
        i === index ? { ...doc, comment: newComment } : doc
      );
    });
  };

  const count = Object.keys(invalidElements).length;
  const isAllValid = count === 0;
  const mainBtnText = isAllValid ? t('button.validate-all') : t('button.ask-new-corrections');

  return (
    <>
      {errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage(null)} />}
      <div className="box-elevations box-elevations--base box-elevations--mb u-mxAuto u-pxm u-pym">
        <h4 className="u-mb0">
          {dossierSummary && 
            <FolderDetailsValidation
              FolderDetail={dossierSummary}
              error={false}
              FolderStatuses={state ? (dossierSummary.status === 'signature' ? [t(`common.platform-validated`)] : [state]) : []}
            />
          }
        </h4>
      </div>
      <Tabs classes="wrap u-mxAuto u-pbb">
        {dossierPresentation !== null &&
          <Tab key="folder" label={t('validation.presentation-dossier')}>
            <PresentationDossier
              state={dossierSummary?.status ?? ''}
              viewPages={dossierPresentation}
              onInvalidateToggle={handleInvalidateToggle}
              onCommentChange={handleCommentChange}
              invalidElements={invalidElements}
            />
          </Tab>
        }
        {receipts !== null && dossierSummary && 
          <Tab key="receipt" label={t('validation.receipts')}>
            <ReceiptsValidation
              dossierId={dossierId}
              state={dossierSummary.status}
              receipts={receipts}
              documents={documents}
              onInvalidateToggle={handleInvalidateToggle}
              onCommentChange={handleCommentChange}
              invalidElements={invalidElements}
              showSignedDocuments={false}
            />
          </Tab>
        }
        {integrationStateHistory !== null &&
          <Tab key="history" label={t('validation.actions')}>
            <IntegrationStateHistoryBlock integrationStateHistory={integrationStateHistory} />
          </Tab>
        }
      </Tabs>
      {EFolderStatus.Pre_signature_validation === dossierSummary?.status &&
        <FooterValidation
          isAllValid={isAllValid}
          count={count}
          mainBtnText={mainBtnText}
          updateAllReceiptsState={updateAllReceiptsState}
          isLoading={isLoading}
        />
      }
    </>
  )
}

export default BlockPreValidationSummaryComponent